import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/axios.ts';
import { toast } from 'react-hot-toast';
import getApiErrorMessage from '../utils/helper.js';

export interface LoanGuarantor {
  id: number;
  loan_id: number;
  user_id: number;
  guaranteed_amount: number;
  status: 'pending' | 'accepted' | 'rejected';
  confirmed_at: string | null;
  created_at: string;
  updated_at: string;
  calculated_guarantor_amount?: number;
  member_savings?: number;
  loan_principal?: number;
  user?: {
    id: number;
    name: string;
    email: string;
  };
  member?: {
    id: number;
    name: string;
    phone: string;
  };
  loan?: {
    id: number;
    principal: number;
    member: {
      name: string;
    };
    loanType: {
      name: string;
    };
  };
}

export interface GuarantorStatus {
  total: number;
  accepted: number;
  pending: number;
  rejected: number;
  all_accepted: boolean;
  required_amount: number;
  confirmed_amount: number;
}

export const useLoanGuarantors = (loanId: number) => {
  return useQuery({
    queryKey: ['loan-guarantors', loanId],
    queryFn: async (): Promise<LoanGuarantor[]> => {
      const response = await api.get(`/loan-guarantors/${loanId}`);
      return response.data.data;
    },
    enabled: !!loanId,
  });
};

export const useAllGuarantorRequests = () => {
  return useQuery({
    queryKey: ['loan-guarantors', 'all'],
    queryFn: async (): Promise<LoanGuarantor[]> => {
      const response = await api.get('/loan-guarantors/pending');
      return response.data.data;
    },
  });
};

export const useGuarantorStatus = (loanId: number) => {
  return useQuery({
    queryKey: ['loan-guarantors', loanId, 'status'],
    queryFn: async (): Promise<GuarantorStatus> => {
      const response = await api.get(`/loan-guarantors/${loanId}/status`);
      return response.data.data;
    },
    enabled: !!loanId,
  });
};

export const useAcceptGuarantor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      loanId, 
      guarantorId, 
      guaranteed_amount 
    }: { 
      loanId: number; 
      guarantorId: number; 
      guaranteed_amount?: number;
    }) => {
      const payload = guaranteed_amount !== undefined ? { guaranteed_amount } : {};
      const response = await api.post(`/loan-guarantors/${loanId}/${guarantorId}/accept`, payload);
      return response.data;
    },
    onSuccess: (data, { loanId }) => {
      toast.success('Guarantor request accepted successfully');
      queryClient.invalidateQueries({ queryKey: ['loan-guarantors', loanId] });
      queryClient.invalidateQueries({ queryKey: ['loan-guarantors', loanId, 'status'] });
      queryClient.invalidateQueries({ queryKey: ['loan-guarantors', 'all'] });
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
};

export const useRejectGuarantor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ loanId, guarantorId }: { loanId: number; guarantorId: number }) => {
      const response = await api.post(`/loan-guarantors/${loanId}/${guarantorId}/reject`);
      return response.data;
    },
    onSuccess: (data, { loanId }) => {
      toast.success('Guarantor request rejected successfully');
      queryClient.invalidateQueries({ queryKey: ['loan-guarantors', loanId] });
      queryClient.invalidateQueries({ queryKey: ['loan-guarantors', loanId, 'status'] });
      queryClient.invalidateQueries({ queryKey: ['loan-guarantors', 'all'] });
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
};

export const useUpdateGuarantorAmount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      loanId, 
      guarantorId, 
      guaranteed_amount 
    }: { 
      loanId: number; 
      guarantorId: number; 
      guaranteed_amount: number;
    }) => {
      const response = await api.put(`/loan-guarantors/${loanId}/${guarantorId}/amount`, {
        guaranteed_amount
      });
      return response.data;
    },
    onSuccess: (data, { loanId }) => {
      toast.success('Guarantor amount updated successfully');
      queryClient.invalidateQueries({ queryKey: ['loan-guarantors', loanId] });
      queryClient.invalidateQueries({ queryKey: ['loan-guarantors', loanId, 'status'] });
      queryClient.invalidateQueries({ queryKey: ['loan-guarantors', 'all'] });
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
};

export const useSendGuarantorRequests = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ 
      loanId, 
      guarantors 
    }: { 
      loanId: number; 
      guarantors: Array<{ user_id: number; guaranteed_amount: number }>;
    }) => {
      const response = await api.post(`/loan-guarantors/${loanId}/send-requests`, {
        guarantors
      });
      return response.data;
    },
    onSuccess: (data, { loanId }) => {
      toast.success('Guarantor requests sent successfully');
      queryClient.invalidateQueries({ queryKey: ['loan-guarantors', loanId] });
      queryClient.invalidateQueries({ queryKey: ['loan-guarantors', loanId, 'status'] });
      queryClient.invalidateQueries({ queryKey: ['member-loans'] });
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
}; 